import { useRef } from 'react'

import { socialLinks } from '@/constants/site'
import { gsap, registerGsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { Reveal, FadeUp } from '@/components/animation/reveal'
import { AnimatedLink } from '@/components/ui/animated-link'
import { ReelCircle } from '@/components/layout/reel-circle'

const HERO_MAIL = 'mailto:hi@mywebsite.com?subject=New%20Project'

export function Hero() {
    const heroRef = useRef<HTMLDivElement>(null)

    // Scroll-linked hero sequence (recreated from the Webflow IX2 scroll keyframes):
    // the dark panel wipes open 0vw -> 100vw, the picture parallaxes 0 -> 20vw, the
    // big variable text thins 900 -> 100 weight, and the headline slides 0 -> -50%.
    // The pin itself is handled by CSS (.main-hero-section-content is position:sticky
    // inside the 300vh .section-hero), so this only scrubs the transforms.
    useIsomorphicLayoutEffect(() => {
        const root = heroRef.current
        if (!root) return

        registerGsap()

        const ctx = gsap.context(() => {
            const panel = root.querySelector('.hero-image-white-text')
            const picture = root.querySelector('.hero-picture')
            const heroTexts = Array.from(
                root.querySelectorAll<HTMLElement>('.home-hero-text')
            )
            const helloTexts = root.querySelectorAll('.hero-hello-text')

            if (panel) gsap.set(panel, { width: '0vw' })
            heroTexts.forEach((el) => {
                el.style.fontVariationSettings = '"wght" 900'
            })

            const tl = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: root,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: true
                }
            })

            if (panel) tl.to(panel, { width: '100vw', duration: 0.5 }, 0.25)
            if (picture) tl.to(picture, { x: '20vw', duration: 0.35 }, 0.4)
            if (heroTexts.length) {
                const wght = { value: 900 }
                tl.to(
                    wght,
                    {
                        value: 100,
                        duration: 0.75,
                        onUpdate: () => {
                            const setting = `"wght" ${Math.round(wght.value)}`
                            heroTexts.forEach((el) => {
                                el.style.fontVariationSettings = setting
                            })
                        }
                    },
                    0.25
                )
            }
            if (helloTexts.length) tl.to(helloTexts, { xPercent: -50, duration: 0.75 }, 0.25)
        }, heroRef)

        return () => ctx.revert()
    }, [])

    return (
        <div ref={heroRef} className='section-hero'>
            <div className='main-hero-section-content'>
                <div className='container home-main-hero-container'>
                    <div className='hero-full-text-panel'>
                        <div className='hero-hello-text'>
                            <Reveal mode='load' delay={0.4} innerClassName='perspective-load1'>
                                <h1 className='home-hero-text'>I&apos;M ANNY</h1>
                            </Reveal>
                        </div>
                    </div>
                    <div className='w-layout-grid hero-grid'>
                        <div
                            id='w-node-c3f32631-6e7f-fd2b-83df-bd58ae7b1a25-78095375'
                            className='hero-left-description-content'
                        >
                            <Reveal mode='load' delay={0.8} innerClassName='perspective-load3'>
                                <div className='big-text'>Elevating brand identities,</div>
                            </Reveal>
                            <Reveal mode='load' delay={1} innerClassName='perspective-load4'>
                                <div className='big-text'>systems, and apps through</div>
                            </Reveal>
                            <Reveal mode='load' delay={1.2} innerClassName='perspective-load5'>
                                <div className='big-text'>collaborative motion design.</div>
                            </Reveal>
                            <div
                                id='w-node-a7f4ec36-8b25-2bbb-bd92-46e5098299e7-78095375'
                                className='hero-links-grid'
                            >
                                <div className='content'>
                                    <Reveal
                                        mode='load'
                                        delay={1.4}
                                        innerClassName='perspective-load6'
                                    >
                                        <AnimatedLink href={HERO_MAIL} className='link'>
                                            <div>hi@mywebsite.com</div>
                                        </AnimatedLink>
                                    </Reveal>
                                </div>
                            </div>
                        </div>
                        <div className='content'>
                            <ReelCircle />
                        </div>
                    </div>
                </div>

                <div className='hero-image-white-text'>
                    <img
                        src='/images/Anny.webp'
                        sizes='(max-width: 2967px) 100vw, 2967px'
                        srcSet='/images/Anny-p-500.png 500w, /images/Anny-p-800.png 800w, /images/Anny-p-1080.png 1080w, /images/Anny-p-1600.png 1600w, /images/Anny-p-2000.png 2000w, /images/Anny-p-2600.png 2600w, /images/Anny.webp 2967w'
                        alt='Anny'
                        className='hero-picture'
                    />
                    <div className='hero-black-gradient' />
                    <div className='hero-container-content'>
                        <div className='container home-main-hero-container'>
                            <div className='hero-full-text-panel'>
                                <div className='hero-hello-text'>
                                    <h1 className='home-hero-text white-hello'>I&apos;M ANNY</h1>
                                </div>
                            </div>
                            <div className='w-layout-grid hero-grid'>
                                <div
                                    id='w-node-cd46b381-382c-db0e-05b8-21bf07ccbceb-78095375'
                                    className='hero-left-description-content'
                                >
                                    <div className='big-text white'>
                                        A product designer living in London, focused on creating
                                        long lasting experiences :)
                                    </div>
                                    <div className='hero-links-grid'>
                                        <div className='content'>
                                            <AnimatedLink
                                                href={HERO_MAIL}
                                                className='link'
                                                underlineClassName='white'
                                            >
                                                <div className='white-text'>hi@mywebsite.com</div>
                                            </AnimatedLink>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    id='w-node-cd46b381-382c-db0e-05b8-21bf07ccbcef-78095375'
                                    className='hero-links-grid right-align-hero-links'
                                >
                                    {socialLinks.map((social) => (
                                        <div className='content' key={social.href}>
                                            <FadeUp>
                                                <AnimatedLink
                                                    href={social.href}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='link'
                                                    underlineClassName='white'
                                                >
                                                    <div className='white-text'>{social.label}</div>
                                                </AnimatedLink>
                                            </FadeUp>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
